/**
 * Toolbar superior premium do Flow Builder
 * Layout CSS Grid com 3 zonas fixas para centralização perfeita das abas
 * Ações: salvar, publicar, testar, voltar
 *
 * PERMISSÕES GRANULARES:
 * - canEditFlow: salvar, publicar, mudar status, configurar gatilhos, editar nome
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Save, 
  Play, 
  ArrowLeft, 
  Settings, 
  Eye, // mantido para uso futuro
  ChevronDown,
  Clock,
  History,
  Image,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { StatusDotLabel } from '@/components/ui/status-dot-label';
import { useNavigate } from 'react-router-dom';
import { FlowBuilderTabs } from './FlowBuilderTabs';

interface FlowToolbarProps {
  flowName: string;
  flowStatus: 'draft' | 'active' | 'paused' | 'archived';
  isSaving?: boolean;
  hasChanges?: boolean;
  /** Permissão para editar o fluxo */
  canEditFlow?: boolean;
  onSave?: () => void;
  onPublish?: () => void;
  onTest?: () => void;
  onViewHistory?: () => void;
  onOpenGallery?: () => void;
  onStatusChange?: (status: 'active' | 'paused' | 'archived') => void;
  onSettingsClick?: () => void;
  onNameChange?: (name: string) => void;
}

/**
 * Indicador de status do fluxo no padrão "bolinha colorida + palavra"
 * (mesmo modelo da listagem de categorias), substituindo o Badge balão.
 */
function StatusBadge({ status }: { status: string }) {
  const statusConfig = {
    draft: { label: 'Rascunho', tone: 'warning' as const },
    active: { label: 'Ativo', tone: 'success' as const },
    paused: { label: 'Pausado', tone: 'warning' as const },
    archived: { label: 'Arquivado', tone: 'neutral' as const },
  };

  const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.draft;

  return <StatusDotLabel tone={config.tone} label={config.label} className="ml-2" />;
}

/** Botão de ação com tooltip (apenas ícone) */
function ActionIconButton({
  icon: Icon,
  label,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick?: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost" size="icon-sm" onClick={onClick}>
          <Icon className="h-4 w-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <p>{label}</p>
      </TooltipContent>
    </Tooltip>
  );
}

/** Toolbar premium com layout grid de 3 zonas */
export function FlowToolbar({
  flowName,
  flowStatus,
  isSaving = false,
  hasChanges = false,
  canEditFlow = true,
  onSave,
  onPublish,
  onTest,
  onViewHistory,
  onOpenGallery,
  onStatusChange,
  onSettingsClick,
  onNameChange,
}: FlowToolbarProps) {
  const navigate = useNavigate();
  const [isEditingName, setIsEditingName] = useState(false);
  const [localName, setLocalName] = useState(flowName);

  /** Salva nome quando usuário termina edição */
  const handleNameBlur = () => {
    setIsEditingName(false);
    if (localName.trim() && localName !== flowName) {
      onNameChange?.(localName.trim());
    } else {
      setLocalName(flowName);
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="h-[52px] border-b border-border/60 bg-card shadow-sm relative flex items-center justify-between px-4">
        {/* Zona esquerda: Voltar + Nome + Status */}
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => navigate('/tenant/flows')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>

          {/* Nome editável só se tem permissão */}
          {isEditingName && canEditFlow ? (
            <Input
              value={localName}
              onChange={(e) => setLocalName(e.target.value)}
              onBlur={handleNameBlur}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleNameBlur();
                if (e.key === 'Escape') {
                  setLocalName(flowName);
                  setIsEditingName(false);
                }
              }}
              className="w-48"
              autoFocus
            />
          ) : (
            <h2
              className={`text-sm font-semibold max-w-[150px] truncate ${
                canEditFlow ? 'cursor-pointer hover:text-primary transition-colors' : ''
              }`}
              onClick={() => canEditFlow && setIsEditingName(true)}
              title={canEditFlow ? 'Clique para editar' : flowName}
            >
              {flowName}
            </h2>
          )}

          <StatusBadge status={flowStatus} />

          {hasChanges && (
            <Badge variant="outline" className="ml-1 text-xs">
              Não salvo
            </Badge>
          )}
        </div>

        {/* Zona central: Abas de navegação (posicionamento absoluto para centralização perfeita) */}
        <div className="absolute left-1/2 -translate-x-1/2">
          <FlowBuilderTabs />
        </div>

        {/* Zona direita: Ações */}
        <div className="flex items-center gap-1">
          {/* Ações utilitárias (ícones compactos) */}
          <ActionIconButton icon={Image} label="Galeria de mídia" onClick={onOpenGallery} />
          <ActionIconButton icon={History} label="Histórico de execuções" onClick={onViewHistory} />
          <ActionIconButton icon={Play} label="Testar fluxo" onClick={onTest} />

          {/* Separador visual */}
          <div className="h-5 border-l border-border/60 mx-1" />

          {/* Ações principais com texto */}
          {canEditFlow && (
            <Button
              variant="outline"
              size="xs"
              onClick={onSave}
              disabled={!hasChanges || isSaving}
            >
              <Save className="h-3.5 w-3.5 mr-1.5" />
              {isSaving ? 'Salvando...' : 'Salvar'}
            </Button>
          )}

          {canEditFlow && (
            <>
              {flowStatus === 'draft' ? (
                <Button size="xs" onClick={onPublish}>
                  <Play className="h-3.5 w-3.5 mr-1.5" />
                  Publicar
                </Button>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="xs">
                      Status <ChevronDown className="h-3.5 w-3.5 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onStatusChange?.('active')}>
                      <Play className="h-4 w-4 mr-2" />
                      Ativar
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onStatusChange?.('paused')}>
                      <Clock className="h-4 w-4 mr-2" />
                      Pausar
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => onStatusChange?.('archived')}>
                      Arquivar
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </>
          )}

          {canEditFlow && (
            <ActionIconButton icon={Settings} label="Configurações" onClick={onSettingsClick} />
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
